import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";

import { PropertiesService } from '@core/services/properties.service';
import { UserService } from '@core/services/user.service';
import { CustomEventService } from '@shared/services/custom-event.service';
import { ImageService } from '@shared/services/image.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';

import { passwordValidator, validateFormGroup } from '@shared/utils/form.util';
import { beforeUploadImage, uploadImage } from '@shared/utils/image-upload.util';
import { createImageUrl } from '@shared/utils/object-mapper.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { RegisterService } from "@core/services/register.service";

@UntilDestroy()
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html'
})
export class ProfileComponent implements OnInit {

  user: ActiveUser.ActiveUserModel;

  passwordForm: FormGroup;
  profileForm: FormGroup;

  userRoles: string;

  // tab index
  selectedIndex = 0;

  oldPasswordVisible = false;
  newPasswordVisible = false;
  confirmNewPasswordVisible = false;

  buttonLoading = false;
  imageLoading: boolean;
  loading = false;

  image = '';
  userId: any;
  sidebarInfo :any;
  updatedsidebarInfo :any;
  userSettingForm: FormGroup;
  parameterValue: any;
  preventChangeStatus: boolean;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private notificationService: NzNotificationService,
    private nzMessageService: NzMessageService,
    private imageService: ImageService,
    private customEventService: CustomEventService,
    private registerService: RegisterService,
    public propertiesService: PropertiesService
  ) {
    this.user = this.userService.activeUser;
    this.userRoles = this.user.roles.map( item => item.cleanName).join(', ')
    this.userSettingForm = this.fb.group({
      parameterValue: [null],
    });
  }

  customImageUploadRequest = (item: NzUploadXHRArgs) => uploadImage(
    item,
    this.imageService,
    this.nzMessageService,
    this.imageLoading,
    (imageUrl) => this.profileForm.get('avatar').setValue(imageUrl)
  );

  beforeUpload = (file: NzUploadFile) => beforeUploadImage(file, this.nzMessageService);

  ngOnInit(): void {
    this.getSidebarInfo();
    this.createForm();
    this.passwordChangeForm();
  }

  createForm(): void {
    let { avatar = '' } = this.user;

    if (avatar) {
      avatar = createImageUrl(avatar);
    }

    this.profileForm = this.fb.group({
      avatar: [avatar, []]
    });
  }

  tabChange($event): void {
    this.selectedIndex = $event;
  }

  passwordChangeForm(): void {
    this.passwordForm = this.fb.group({
      password: [null, [Validators.required]],
      newPassword: [null, [Validators.required, passwordValidator, this.passwordControlValidator]],
      confirm: ['' , [Validators.required, this.confirmValidator]],
    });
  }

  confirmValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { error: true, required: true };
    } else if (control.value !== this.passwordForm.controls.newPassword.value) {
      return { confirm: true, error: true };
    }
    return {};
  };

  passwordControlValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { error: true, required: true };
    } else if (control.value === this.passwordForm.controls.password.value) {
      return { newPassword: true, error: true };
    }
    return {};
  };

  submitForm(): void {
    if (this.propertiesService.PLAYGROUND) {
      return;
    }

    if (this.selectedIndex === 0) {
      validateFormGroup(this.profileForm);

      if (this.profileForm.invalid) {
        return;
      }

      const formData = {
        ...this.profileForm.value,
        id: this.user.id
      };

      this.buttonLoading = true;
      this.userService.updateAvatar(formData)
        .pipe(untilDestroyed(this))
        .subscribe(_ => {
          this.buttonLoading = false;
          this.customEventService.announceEvent(ActionTypesEnum.UPDATE_AVATAR);
        }, error => {
          this.buttonLoading = false;
          console.log('error while updating user avatar', error);
        });
    } else {
      validateFormGroup(this.passwordForm);

      if (this.passwordForm.invalid) {
        return;
      }

      const formData = {
        ...this.passwordForm.value,
        id: this.user.id
      };

      this.buttonLoading = true;
      this.userService.changePassword(formData)
        .pipe(untilDestroyed(this))
        .subscribe(_ => {
          this.buttonLoading = false;
          this.selectedIndex = 0;
        }, error => {
          this.buttonLoading = false;
          console.log('error while updating user password', error);
        });
    }
  }

  getUserId(): Promise<any> {
    return new Promise(resolve => {
    this.userService.getAuthenticatedUser().pipe(untilDestroyed(this))
      .subscribe((res) => {
        resolve(this.userId = res.id);
      }, error => {
        console.log('error while fetching user data', error);
        });
      });
  }

  async getSidebarInfo(): Promise<void> {
    await this.getUserId()
    this.userService.getSidebarInfo(this.userId, 'MENU_TYPE').pipe(untilDestroyed(this))
      .subscribe( response => {
        this.buttonLoading = false;
        this.sidebarInfo = response;
        if (this.sidebarInfo.parameterValue === 'COLLAPSE') {
          this.setValueWithoutChangeStatus(true);
        } else {
          this.setValueWithoutChangeStatus(false);
        }
      }, error => {
        this.buttonLoading = false;
        console.log('error while getting menu style', error);
      });
  }

  setValueWithoutChangeStatus(value: any): void {
    this.preventChangeStatus = true;
    this.userSettingForm.get('parameterValue').setValue(value);
    this.preventChangeStatus = false;
  }

  changeStatus(value): void {

    if (this.preventChangeStatus) {
      return;
    }


    value ? this.sidebarInfo.parameterValue = 'COLLAPSE'
      : this.sidebarInfo.parameterValue = 'HAMBURGER'

    this.userService.updateSidebarInfo(this.sidebarInfo.userId,this.sidebarInfo.parameterKey, this.sidebarInfo.parameterValue).pipe(untilDestroyed(this))
      .subscribe( response => {
        this.buttonLoading = false;
        this.updatedsidebarInfo = response;
        this.customEventService.announceEvent(ActionTypesEnum.CHANGE_MENU_STYLE ,this.updatedsidebarInfo);
      }, error => {
        this.buttonLoading = false;
        console.log('error while updating menu style', error);
      });

  }

  validateConfirmPassword(): void {
    setTimeout(() => this.passwordForm.controls.confirm.updateValueAndValidity());
  }

  toggleOldPasswordVisibility() {
    this.oldPasswordVisible = !this.oldPasswordVisible;
  }

  toggleNewPasswordVisibility() {
    this.newPasswordVisible = !this.newPasswordVisible;
  }

  toggleConfirmNewPasswordVisibility() {
    this.confirmNewPasswordVisible = !this.confirmNewPasswordVisible;
  }

  resetForm(): void {
    this.selectedIndex === 0 ? this.createForm() : this.passwordForm.reset();
  }
}
